import { SearchCriterion, SearchQuery, StringCriterion } from '@neptune/search-query-domain';

import { SearchQueryTermType, SearchQueryTermWithoutPositions } from './types';

export const createQueryMapper = () => {
  function mapRegexQueryToSearchQuery(regexQuery: SearchQueryTermWithoutPositions[]): SearchQuery {
    const parsedQuery = parseExpression(regexQuery);

    if (!parsedQuery) {
      return {
        criteria: [],
        operator: 'and',
      };
    }

    return normalizeToSearchQuery(parsedQuery);
  }

  function createStringCriterion(value: string, isNegated: boolean): StringCriterion {
    return {
      attribute: 'sys/name',
      type: 'string',
      operator: isNegated ? '!matches' : 'matches',
      value,
    };
  }

  function normalizeToSearchQuery(node: SearchQuery | SearchCriterion): SearchQuery {
    if (isSearchQuery(node)) {
      return node;
    }

    return {
      criteria: [node],
      operator: 'and',
    };
  }

  function isSearchQuery(node: SearchQuery | SearchCriterion): node is SearchQuery {
    return 'criteria' in node;
  }

  function isStringCriterion(node: SearchCriterion): node is StringCriterion {
    return node.type === 'string';
  }

  function parseExpression(terms: SearchQueryTermWithoutPositions[]) {
    let index = 0;

    const parseOrExpression = (): SearchQuery | SearchCriterion | null => {
      const left = parseAndExpression();

      if (!left) {
        return null;
      }

      const criteria = [left];

      while (matchOperator('OR')) {
        const right = parseAndExpression();

        if (right) {
          criteria.push(right);
        }
      }

      if (criteria.length === 1) {
        return criteria[0];
      }

      return {
        criteria,
        operator: 'or',
      };
    };

    const parseAndExpression = (): SearchQuery | SearchCriterion | null => {
      const left = parseUnaryExpression();

      if (!left) {
        return null;
      }

      const criteria = [left];

      while (matchOperator('AND')) {
        const right = parseUnaryExpression();

        if (right) {
          criteria.push(right);
        }
      }

      if (criteria.length === 1) {
        return criteria[0];
      }

      return {
        criteria,
        operator: 'and',
      };
    };

    const parseUnaryExpression = (): SearchQuery | SearchCriterion | null => {
      while (isIgnorableToken(peek())) {
        index += 1;
      }

      if (matchOperator('NOT')) {
        const operand = parseUnaryExpression();

        if (!operand) {
          return null;
        }

        return negateNode(operand);
      }

      return parsePrimary();
    };

    const parsePrimary = (): SearchQuery | SearchCriterion | null => {
      const term = peek();

      if (!term) {
        return null;
      }

      if (term.type === SearchQueryTermType.GROUP_START) {
        index += 1;
        const grouped = parseOrExpression();

        if (peek()?.type === SearchQueryTermType.GROUP_END) {
          index += 1;
        }

        return grouped;
      }

      if (term.type === SearchQueryTermType.CRITERION) {
        index += 1;
        return createStringCriterion(term.value, false);
      }

      if (term.type === SearchQueryTermType.GROUP_END) {
        index += 1;
        return null;
      }

      return null;
    };

    const peek = () => terms[index];

    const matchOperator = (operator: 'AND' | 'OR' | 'NOT') => {
      const term = peek();

      if (!term || term.type !== SearchQueryTermType.OPERATOR || term.value !== operator) {
        return false;
      }

      index += 1;
      return true;
    };

    const isIgnorableToken = (term?: SearchQueryTermWithoutPositions) =>
      term?.type === SearchQueryTermType.OPERATOR && (term.value === 'AND' || term.value === 'OR');

    return parseOrExpression();
  }

  function negateNode(node: SearchQuery | SearchCriterion): SearchQuery | SearchCriterion {
    if (!isSearchQuery(node)) {
      if (!isStringCriterion(node)) {
        return node;
      }

      let nextOperator = node.operator;

      if (node.operator === 'matches') {
        nextOperator = '!matches';
      } else if (node.operator === '!matches') {
        nextOperator = 'matches';
      }

      return { ...node, operator: nextOperator };
    }

    return {
      criteria: node.criteria.map((criterion) => negateNode(criterion)),
      operator: node.operator === 'and' ? 'or' : 'and',
    };
  }

  return { mapRegexQueryToSearchQuery };
};

export const queryMapper = createQueryMapper();
